<script setup lang="ts">
  import { createFlatBaseColumns } from '@/utils/table'
  import {
    Modal,
    Table,
    UiButton,
    FormInput,
    ConfirmationDialog,
    type AddressBookOrganization,
    type Address,
    AddressBookOrganizationType,
    DropDown,
    useToastStore,
  } from '@logistiek-zonder-papier/lzp-library'
  import { computed, onMounted, ref } from 'vue'
  import { useRouter } from 'vue-router'
  import { useAddressBookStore } from '@/store/address-book.store'
  import type { UUID } from 'crypto'
  import AddressComponent from '@/components/AddressComponent.vue'
  import {
    createEmptyAddress,
    createEmptyAddressBookOrganization,
    normalizeOrganization,
    type CreateAddressBookOrganization,
  } from '@/utils/address-utils'
  import { useServerValidation } from '@/composables/validation/use-server-validation'
  import { formatAddressErrors } from '@/utils/server-error'
  import { isAxiosError } from 'axios'
  import TopBar from '@/components/TopBar.vue'

  const MAX_PROJECT_NAME_LENGTH = 150

  const addressBookStore = useAddressBookStore()
  const showAddressBookModal = ref(false)
  const organisationToDelete = ref<AddressBookOrganization | null>(null)

  const newOrganisation = ref<CreateAddressBookOrganization>(createEmptyAddressBookOrganization())
  const router = useRouter()
  const toastStore = useToastStore()
  const validation = useServerValidation()
  const addressValidation = validation.scope('address', formatAddressErrors)
  addressValidation.clearOnChange(() => newOrganisation.value.address)

  async function createAddessBookOrganization() {
    try {
      await addressBookStore.createAddressBookOrganization(
        normalizeOrganization(newOrganisation.value)
      )
      await addressBookStore.fetchAddressBooks()
      showAddressBookModal.value = false
      newOrganisation.value = createEmptyAddressBookOrganization()
      toastStore.success('Organisatie succesvol aangemaakt')
    } catch (error) {
      const handled =
        isAxiosError(error) &&
        error.response?.status === 400 &&
        validation.setFromResponse(error.response.data)
      if (!handled) {
        toastStore.error('Fout bij het aanmaken van de organisatie')
        throw error
      }
    }
  }
  function openDeleteDialog(organisation: AddressBookOrganization) {
    organisationToDelete.value = organisation
  }

  async function deleteOrganisation(id: UUID) {
    try {
      await addressBookStore.deleteAddressBookOrganization(id)
      organisationToDelete.value = null
      toastStore.success('Organisatie succesvol verwijderd')
    } catch (error) {
      toastStore.error('Error deleting organisation:')
      throw error
    }
  }

  onMounted(async () => {
    await addressBookStore.fetchAddressBooks()
  })

  const placeholderRow: AddressBookOrganization = {
    id: crypto.randomUUID(),
    customerId: crypto.randomUUID(),
    name: '',
    vihbNumber: null,
    address: { ...createEmptyAddress(), id: crypto.randomUUID(), customerId: null },
    types: [],
    locations: [],
  }

  const allKeys: (keyof AddressBookOrganization)[] = [
    'id',
    'customerId',
    'name',
    'vihbNumber',
    'address',
    'types',
    'locations',
  ]

  const selectedKeys: (keyof AddressBookOrganization)[] = ['name', 'customerId', 'id']

  const columns = computed(() => {
    const rows = addressBookStore.addressBookOrganisations
    const hasData = rows.length > 0

    const result = createFlatBaseColumns(
      hasData ? rows : [placeholderRow],
      hasData ? ['deletedAt', 'createdAt', 'updatedAt', 'customPropertyGroupId'] : [],
      {
        searchableKeys: hasData ? selectedKeys : allKeys,
        columnOrder: hasData ? selectedKeys : allKeys,
      }
    )

    if (hasData) result.push({ key: 'action', label: 'actie' })
    return result
  })

  async function navigate(organisationId: UUID) {
    router.push({
      name: 'organisation-details',
      params: { id: organisationId },
    })
  }

  const showConfirmationDialog = computed({
    get: () => organisationToDelete.value != null,
    set: value => {
      if (!value) organisationToDelete.value = null
    },
  })

  function formatAddressForTable(address: Address) {
    if (!address) return
    let result = `${address.street} ${address.houseNumber}`

    if (address.suffix) {
      result += address.suffix
    }

    result += `, ${address.postalCode} ${address.city}`

    if (address.country) {
      result += `, ${address.country}`
    }

    return result
  }

  type TypeOption<T extends string> = { value: T; label: string }
  type OrganizationTypeOption = TypeOption<AddressBookOrganizationType>

  const organizationTypeOptions: OrganizationTypeOption[] = createTypeOptions(
    AddressBookOrganizationType
  )

  function createTypeOptions<T extends string>(enumObject: Record<string, T>): TypeOption<T>[] {
    return Object.values(enumObject).map(value => ({
      value,
      label: value.charAt(0).toUpperCase() + value.slice(1),
    }))
  }

  const selectedTypes = computed<OrganizationTypeOption[]>({
    get: () =>
      organizationTypeOptions.filter(option =>
        newOrganisation.value?.types?.includes(option.value)
      ),
    set: items => {
      if (!newOrganisation.value) return
      const selected = new Set(items.map(item => item.value))
      newOrganisation.value.types = organizationTypeOptions
        .filter(option => selected.has(option.value))
        .map(option => option.value)
    },
  })
</script>

<template>
  <TopBar />
  <Modal v-model:show="showAddressBookModal">
    <template #header>
      <h3>Nieuwe organisatie</h3>
    </template>
    <FormInput
      v-model="newOrganisation.name"
      label="Organisatie naam"
      placeholder="Voer de organisatie naam in"
      type="text"
      :error="validation.errors.value['name']"
      :maxLength="MAX_PROJECT_NAME_LENGTH"
    />
    <FormInput
      v-model="newOrganisation.vihbNumber"
      label="VIHB nummer"
      placeholder="Voer het VIHB nummer in"
    />
    <DropDown
      label="Organisatie type"
      idKey="value"
      :allowNew="false"
      :allowMultiselect="true"
      v-model:multiselectData="selectedTypes"
      :data="organizationTypeOptions"
    >
      <template #empty>
        <div class="item">Selecteer organisatie type(s)</div>
      </template>
      <template #multipleItems="{ items }">
        <div class="item-container">
          <span v-for="chosenItem in items" :key="chosenItem?.value" class="item">
            {{ chosenItem?.label }}
          </span>
        </div>
      </template>
      <template #item="{ item }">
        <div
          class="selection-option"
          :class="selectedTypes.some(type => type.value === item?.value) && 'selected-option'"
          @click.stop="
            selectedTypes = selectedTypes.some(type => type.value === item?.value)
              ? selectedTypes.filter(type => type.value !== item?.value)
              : [...selectedTypes, item]
          "
        >
          <span class="item-name">{{ item?.label }}</span>
        </div>
      </template>
    </DropDown>

    <AddressComponent v-model="newOrganisation.address" :errors="addressValidation.errors.value" />

    <template #footer>
      <UiButton label="Opslaan" variant="prominent" @click="createAddessBookOrganization" />
    </template>
  </Modal>

  <div v-if="addressBookStore.addressBookOrganisations" class="address-book-container">
    <div v-if="addressBookStore.addressBookOrganisations.length === 0">No organisations found</div>
    <Table
      v-show="addressBookStore.addressBookOrganisations.length > 0"
      :rows="addressBookStore.addressBookOrganisations"
      :columns="columns"
      :configurable="true"
      configuration-storage-key="AddressBookTableConfig"
      row-key="id"
    >
      <template #table-top-actions>
        <UiButton
          label="Nieuwe organisatie"
          variant="prominent"
          @click="showAddressBookModal = true"
          icon="plus"
        />
      </template>

      <template #cell-address="{ row }">
        {{ formatAddressForTable(row.address) }}
      </template>

      <template #cell-locations="{ row }">
        <div v-for="(location, index) in row.locations" :key="index">
          {{ formatAddressForTable(location.address) }}
        </div>
      </template>
      <template #cell-types="{ row }">
        <div v-if="row.types && row.types.length > 0">
          <span v-for="(type, index) in row.types" :key="index">
            {{ type }}<span v-if="index < row.types.length - 1">, </span>
          </span>
        </div>
      </template>

      <template #cell-action="{ row }">
        <div class="action-row">
          <UiButton
            label="Details"
            variant="prominent"
            icon="chevronRight"
            @click="navigate(row.id)"
          />

          <UiButton variant="negative" @click="openDeleteDialog(row)" icon="delete" />
        </div>
      </template>
    </Table>

    <ConfirmationDialog
      :title="`Are you sure you want to remove: ${organisationToDelete?.name}?`"
      body="This action cannot be undone."
      confirmButtonText="Remove"
      confirmButtonColor="negative"
      v-model:show="showConfirmationDialog"
      :confirm="() => organisationToDelete && deleteOrganisation(organisationToDelete.id!)"
    />
  </div>
</template>

<style scoped lang="css">
  .address-book-container {
    padding: var(--spacing-m);
  }

  .action-row {
    display: flex;
    flex-direction: row;
    gap: var(--spacing-s);
  }

  .item-container {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: start;
  }

  .item {
    padding: var(--spacing-xs);
  }

  .selection-option {
    display: flex;
    justify-content: space-between;
    gap: var(--spacing-xs);
    padding: var(--spacing-xs);
  }

  .selection-option .item-name {
    flex: 1;
  }

  .selected-option {
    font-weight: bold;
  }
</style>
